import React, { useState, useEffect, useRef } from 'react';
import { reportsAPI, clientAPI, taskAPI } from '../api/api';
import { FileText, Building2, DollarSign, CheckCircle, Clock, Download, Search, Printer, ChevronDown, ChevronRight, Banknote, History, Receipt } from 'lucide-react';
import AllTasksReport from './AllTasksReport';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('all-tasks');
  const [auditLogs, setAuditLogs] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientReport, setClientReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [clientSearch, setClientSearch] = useState('');
  const reportRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs();
    }
    fetchClients(); // Always fetch clients for the search
  }, [activeTab]);

  // Handle URL params for direct client link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const clientId = params.get('clientId');
    
    if (tab === 'client') {
      setActiveTab('client');
      if (clientId) {
        setSelectedClient(clientId);
        fetchClientReport(clientId);
      }
    }
  }, []);

  const fetchAuditLogs = async () => {
    try {
      const response = await reportsAPI.getAuditTrail({ limit: 100 });
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchClientReport = async (clientId) => {
    if (!clientId) return;
    setLoading(true);
    try {
      const response = await reportsAPI.getClientWorkReport(clientId);
      setClientReport(response.data);
    } catch (error) {
      console.error('Failed to fetch client report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    if (clientId) {
      fetchClientReport(clientId);
    } else {
      setClientReport(null);
    }
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Filter clients by search and sort alphabetically
  const filteredClients = clients
    .filter(client =>
      client.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.client_code?.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.code?.toLowerCase().includes(clientSearch.toLowerCase())
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Export to CSV
  const handleExportCSV = () => {
    if (!clientReport) return;
    
    const headers = ['Date', 'Task Name', 'Service', 'Task Type', 'Status', 'Time Spent', 'Invoice Amount', 'Receipt Amount'];
    const rows = clientReport.tasks.map(task => [
      formatDate(task.created_at),
      task.task_name || '-',
      task.service_name,
      task.task_type_name,
      task.status,
      formatTime(task.total_time_minutes || 0),
      task.invoice?.amount || 0,
      task.receipt?.amount || 0
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clientReport.client.name}_work_report.csv`;
    a.click();
  };

  // Print report (without task history)
  // CA Letterhead for print pages
  const caLogoUrl = window.location.origin + '/images/ca-logo.png';
  const letterheadHtml = `
    <div style="text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #1e40af;">
      <img src="${caLogoUrl}" alt="CA" style="height: 50px; margin-bottom: 8px;" />
      <h2 style="margin: 0; color: #1e40af; font-size: 20px; letter-spacing: 2px;">HYDER ALI AND ASSOCIATES</h2>
      <p style="margin: 4px 0 0 0; color: #6b7280; font-size: 13px; letter-spacing: 1px;">CHARTERED ACCOUNTANTS</p>
    </div>
  `;

  const handlePrint = (includeHistory = false) => {
    if (reportRef.current) {
      // Clone the content
      const clonedContent = reportRef.current.cloneNode(true);
      
      // If not including history, remove all task-history sections
      if (!includeHistory) {
        const historySections = clonedContent.querySelectorAll('.task-history-section');
        historySections.forEach(section => section.remove());
      } else {
        // If including history, we need to expand all tasks first
        // The history sections are already in the DOM, just need to show them
      }
      
      const printContent = clonedContent.innerHTML;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Client Work Report - ${clientReport?.client?.name}${includeHistory ? ' (with History)' : ''}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f5f5f5; }
              .summary-card { display: inline-block; margin: 10px; padding: 15px; border: 1px solid #ddd; }
              h1 { color: #333; }
              .subtask-container { margin-top: 10px; padding: 10px; background: #f9f9f9; border-radius: 4px; }
              .subtask-item { padding: 6px 0; border-bottom: 1px dashed #ddd; display: flex; align-items: center; }
              .subtask-item:last-child { border-bottom: none; }
              .subtask-number { font-weight: bold; margin-right: 10px; min-width: 25px; }
              .subtask-completed { color: #22c55e; }
              .subtask-pending { color: #666; }
              .subtask-info { font-size: 0.85em; color: #888; margin-left: 10px; }
              .task-history-section { margin-top: 10px; padding: 10px; background: #eff6ff; border-radius: 4px; page-break-inside: avoid; }
              .history-item { padding: 6px 0; border-bottom: 1px dashed #bfdbfe; display: flex; align-items: flex-start; gap: 8px; }
              .history-item:last-child { border-bottom: none; }
              .history-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; flex-shrink: 0; background: #60a5fa; }
              .history-dot-green { background: #22c55e; }
              .history-dot-amber { background: #f59e0b; }
              .history-dot-purple { background: #a855f7; }
              .history-action { font-weight: 500; color: #1f2937; }
              .history-details { font-size: 0.85em; color: #4b5563; }
              .history-meta { font-size: 0.8em; color: #6b7280; }
              .task-expanded-content { display: block !important; }
              .reimbursement-section, .receipt-section { margin-top: 10px; padding: 10px; border-radius: 4px; }
              .reimbursement-section { background: #fef3c7; }
              .receipt-section { background: #d1fae5; }
              @media print {
                .task-history-section { page-break-inside: avoid; }
                .no-print { display: none !important; }
              }
            </style>
          </head>
          <body>${letterheadHtml}${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Generate printable content with all task history expanded
  const handlePrintWithHistory = () => {
    if (!clientReport) return;
    
    const printWindow = window.open('', '_blank');
    
    // Build history for all tasks
    const buildTaskHistory = (task) => {
      const historyItems = [];
      
      // Task Creation
      if (task.created_at) {
        historyItems.push({
          action: 'Task Created',
          details: task.task_name || task.task_type_name,
          user: task.created_by === 'system' ? 'System' : (task.created_by_name || 'User'),
          timestamp: task.created_at,
          type: 'create'
        });
      }
      
      // Non-billable status
      if (task.is_billable === false) {
        historyItems.push({
          action: 'Marked as Non-Billable',
          details: task.non_billable_reason || 'No reason provided',
          user: task.non_billable_by || 'User',
          timestamp: task.non_billable_at || task.updated_at,
          type: 'status'
        });
      }
      
      // Invoice
      if (task.invoice) {
        historyItems.push({
          action: 'Invoice Generated - Rs.' + (task.invoice.amount?.toLocaleString() || 0),
          details: 'Invoice No: ' + task.invoice.number,
          user: 'User',
          timestamp: task.invoice.date,
          type: 'invoice'
        });
      }
      
      // Discount
      if (task.discount) {
        historyItems.push({
          action: 'Discount Applied - Rs.' + (task.discount.amount?.toLocaleString() || 0),
          details: task.discount.reason || 'No reason',
          user: task.discount.added_by || 'User',
          timestamp: task.discount.added_at || task.discount.date,
          type: 'discount'
        });
      }
      
      // Reimbursements
      (task.reimbursements || []).forEach((reimb) => {
        historyItems.push({
          action: 'Reimbursement Added - Rs.' + (reimb.amount?.toLocaleString() || 0),
          details: reimb.type + ': ' + (reimb.description || 'N/A'),
          user: reimb.added_by || 'User',
          timestamp: reimb.added_at || reimb.date,
          type: 'reimbursement'
        });
        
        if (reimb.status === 'Collected') {
          historyItems.push({
            action: 'Reimbursement Collected - Rs.' + (reimb.amount?.toLocaleString() || 0),
            details: reimb.type + ': ' + (reimb.description || 'N/A'),
            user: reimb.collected_by || 'User',
            timestamp: reimb.collected_at,
            type: 'reimbursement_collected'
          });
        }
      });
      
      // Receipts
      (task.receipts || []).forEach((receipt) => {
        historyItems.push({
          action: 'Receipt Collected - Rs.' + (receipt.amount?.toLocaleString() || 0),
          details: 'Receipt No: ' + receipt.number + ', Mode: ' + receipt.mode,
          user: 'User',
          timestamp: receipt.date,
          type: 'receipt'
        });
      });
      
      // Audit trail
      (task.audit_trail || []).forEach(log => {
        historyItems.push({
          action: log.action,
          details: log.details || '',
          user: log.user_name || 'User',
          timestamp: log.timestamp,
          type: 'audit'
        });
      });
      
      // Sort by timestamp (newest first)
      historyItems.sort((a, b) => {
        const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
        const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
        return dateB - dateA;
      });
      
      return historyItems;
    };
    
    const getTypeColor = (type) => {
      switch(type) {
        case 'create': return '#22c55e';
        case 'invoice': return '#3b82f6';
        case 'receipt': return '#10b981';
        case 'reimbursement': return '#f59e0b';
        case 'reimbursement_collected': return '#d97706';
        case 'discount': return '#a855f7';
        case 'status': return '#6b7280';
        default: return '#3b82f6';
      }
    };
    
    // Build tasks HTML
    let tasksHtml = '';
    clientReport.tasks.forEach((task) => {
      const historyItems = buildTaskHistory(task);
      
      let historyHtml = '';
      if (historyItems.length > 0) {
        historyHtml = '<div style="background: #eff6ff; border-radius: 6px; padding: 10px; margin-top: 10px;">' +
          '<div style="font-size: 11px; font-weight: 600; color: #1d4ed8; margin-bottom: 8px; text-transform: uppercase;">Task History (' + historyItems.length + ' events)</div>';
        
        historyItems.forEach(item => {
          historyHtml += '<div style="display: flex; align-items: flex-start; gap: 8px; padding: 6px 0; border-bottom: 1px dashed #bfdbfe;">' +
            '<div style="width: 8px; height: 8px; border-radius: 50%; background: ' + getTypeColor(item.type) + '; margin-top: 4px; flex-shrink: 0;"></div>' +
            '<div style="flex: 1;">' +
            '<div style="font-size: 12px; font-weight: 500; color: #1f2937;">' + item.action + '</div>' +
            (item.details ? '<div style="font-size: 11px; color: #4b5563;">' + item.details + '</div>' : '') +
            '<div style="font-size: 10px; color: #6b7280;">' + item.user + ' - ' + (item.timestamp ? formatDateTime(item.timestamp) : 'N/A') + '</div>' +
            '</div></div>';
        });
        historyHtml += '</div>';
      }
      
      const statusBg = task.status === 'Completed' || task.status === 'Done' ? '#dcfce7' : task.is_billable === false ? '#f3f4f6' : '#dbeafe';
      const statusColor = task.status === 'Completed' || task.status === 'Done' ? '#166534' : task.is_billable === false ? '#374151' : '#1e40af';
      const statusText = task.is_billable === false ? 'Non-billable' : task.status;
      const invoiceAmount = task.invoice ? 'Rs.' + task.invoice.amount.toLocaleString() : '-';
      
      tasksHtml += '<div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; margin-bottom: 15px; page-break-inside: avoid;">' +
        '<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">' +
        '<div><strong style="font-size: 14px;">' + (task.task_name || task.task_type_name) + '</strong>' +
        '<div style="font-size: 12px; color: #6b7280;">' + task.service_name + ' - ' + formatDate(task.created_at) + '</div></div>' +
        '<div style="text-align: right;"><span style="padding: 2px 8px; border-radius: 4px; font-size: 11px; background: ' + statusBg + '; color: ' + statusColor + ';">' + statusText + '</span>' +
        '<div style="font-size: 12px; margin-top: 4px;">' + invoiceAmount + '</div></div></div>' +
        historyHtml + '</div>';
    });
    
    // Calculate reimbursements
    let totalReimbursements = 0;
    let pendingReimbursements = 0;
    clientReport.tasks.forEach(task => {
      (task.reimbursements || []).forEach(r => {
        totalReimbursements += r.amount || 0;
        if (r.status === 'Pending') pendingReimbursements += r.amount || 0;
      });
    });
    
    let reimbHtml = '';
    if (totalReimbursements > 0) {
      reimbHtml = '<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 20px;">' +
        '<div style="background: #fef3c7; padding: 10px; border-radius: 6px;"><div style="font-size: 11px; color: #92400e;">Total Reimbursements</div><div style="font-size: 18px; font-weight: bold; color: #d97706;">Rs.' + totalReimbursements.toLocaleString() + '</div></div>' +
        '<div style="background: #fef3c7; padding: 10px; border-radius: 6px;"><div style="font-size: 11px; color: #92400e;">Pending Collection</div><div style="font-size: 18px; font-weight: bold; color: #b45309;">Rs.' + pendingReimbursements.toLocaleString() + '</div></div></div>';
    }
    
    const htmlContent = '<html><head><title>Client Work Report (with History) - ' + clientReport.client.name + '</title>' +
      '<style>body { font-family: Arial, sans-serif; padding: 20px; font-size: 13px; } @media print { body { padding: 10px; } }</style></head>' +
      '<body><div style="text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #1e40af;">' +
      '<img src="' + caLogoUrl + '" alt="CA" style="height: 50px; margin-bottom: 8px;" />' +
      '<h2 style="margin: 0; color: #1e40af; font-size: 20px; letter-spacing: 2px;">HYDER ALI AND ASSOCIATES</h2>' +
      '<p style="margin: 4px 0; color: #6b7280; font-size: 13px; letter-spacing: 1px;">CHARTERED ACCOUNTANTS</p>' +
      '<h3 style="margin: 10px 0 0 0;">Client Work Report (with History)</h3></div>' +
      '<div style="background: #f9fafb; padding: 15px; border-radius: 8px; margin-bottom: 20px;"><h3 style="margin: 0 0 5px 0;">' + clientReport.client.name + '</h3>' +
      '<p style="margin: 0; color: #6b7280;">Category: ' + clientReport.client.category + ' | Code: ' + (clientReport.client.code || clientReport.client.client_code || '-') + '</p></div>' +
      '<div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 20px;">' +
      '<div style="background: #dbeafe; padding: 10px; border-radius: 6px; text-align: center;"><div style="font-size: 11px; color: #1e40af;">Total Tasks</div><div style="font-size: 18px; font-weight: bold;">' + clientReport.summary.total_tasks + '</div></div>' +
      '<div style="background: #fef3c7; padding: 10px; border-radius: 6px; text-align: center;"><div style="font-size: 11px; color: #92400e;">Pending</div><div style="font-size: 18px; font-weight: bold;">' + clientReport.summary.pending_tasks + '</div></div>' +
      '<div style="background: #dcfce7; padding: 10px; border-radius: 6px; text-align: center;"><div style="font-size: 11px; color: #166534;">Completed</div><div style="font-size: 18px; font-weight: bold;">' + clientReport.summary.completed_tasks + '</div></div>' +
      '<div style="background: #dbeafe; padding: 10px; border-radius: 6px; text-align: center;"><div style="font-size: 11px; color: #1e40af;">Total Invoiced</div><div style="font-size: 18px; font-weight: bold;">Rs.' + (clientReport.summary.total_invoiced?.toLocaleString() || 0) + '</div></div>' +
      '<div style="background: #dcfce7; padding: 10px; border-radius: 6px; text-align: center;"><div style="font-size: 11px; color: #166534;">Total Received</div><div style="font-size: 18px; font-weight: bold;">Rs.' + (clientReport.summary.total_received?.toLocaleString() || 0) + '</div></div></div>' +
      reimbHtml +
      '<h3 style="margin: 20px 0 10px 0; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px;">All Tasks with History</h3>' +
      tasksHtml +
      '<div style="margin-top: 30px; text-align: center; color: #6b7280; font-size: 11px;">Generated on ' + new Date().toLocaleString() + '</div></body></html>';
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="space-y-6">
      <div className="flex space-x-4 border-b">
        <button
          onClick={() => setActiveTab('all-tasks')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'all-tasks'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="all-tasks-tab"
        >
          All Tasks
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'audit'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="audit-trail-tab"
        >
          Audit Trail
        </button>
        <button
          onClick={() => setActiveTab('client')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'client'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="client-work-tab"
        >
          Client Work Report
        </button>
      </div>

      {/* All Tasks Tab */}
      {activeTab === 'all-tasks' && <AllTasksReport />}

      {/* Audit Trail Tab */}
      {activeTab === 'audit' && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Recent Activities</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {auditLogs.map((log, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDateTime(log.timestamp)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {log.user_name}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded ${
                        log.action === 'CREATE' ? 'bg-green-100 text-green-700' :
                        log.action === 'UPDATE' ? 'bg-blue-100 text-blue-700' :
                        log.action === 'DELETE' ? 'bg-red-100 text-red-700' :
                        log.action.includes('START') ? 'bg-purple-100 text-purple-700' :
                        log.action.includes('STOP') ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">
                      {log.entity_type}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {log.details}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {auditLogs.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No audit logs found.</p>
            </div>
          )}
        </div>
      )}

      {/* Client Work Report Tab */}
      {activeTab === 'client' && (
        <div className="space-y-6">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2">Search & Select Client</label>
                <input
                  type="text"
                  placeholder="Search by client name..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg mb-2"
                />
                <select
                  value={selectedClient}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="client-select"
                >
                  <option value="">Choose a client...</option>
                  {filteredClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} {client.client_code ? `(${client.client_code})` : client.code ? `(${client.code})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {clientReport && (
                <div className="flex gap-2">
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => handlePrint(false)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    title="Print without task history"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </button>
                  <button
                    onClick={handlePrintWithHistory}
                    className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    title="Print with complete task history"
                  >
                    <History className="w-4 h-4 mr-2" />
                    Print with History
                  </button>
                </div>
              )}
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}

          {!loading && clientReport && (
            <div ref={reportRef} className="space-y-6">
              {/* Client Info */}
              <div className="bg-white p-6 rounded-lg shadow border">
                <h2 className="text-xl font-bold mb-2">{clientReport.client.name}</h2>
                <p className="text-gray-600">
                  Category: <span className="font-medium">{clientReport.client.category}</span>
                  {clientReport.client.code && ` | Code: ${clientReport.client.code}`}
                </p>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center space-x-2 text-blue-600 mb-2">
                    <FileText className="w-5 h-5" />
                    <p className="text-sm font-medium">Total Tasks</p>
                  </div>
                  <p className="text-2xl font-bold">{clientReport.summary.total_tasks}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center space-x-2 text-yellow-600 mb-2">
                    <Clock className="w-5 h-5" />
                    <p className="text-sm font-medium">Pending Tasks</p>
                  </div>
                  <p className="text-2xl font-bold">{clientReport.summary.pending_tasks}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center space-x-2 text-green-600 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <p className="text-sm font-medium">Completed</p>
                  </div>
                  <p className="text-2xl font-bold">{clientReport.summary.completed_tasks}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center space-x-2 text-purple-600 mb-2">
                    <Clock className="w-5 h-5" />
                    <p className="text-sm font-medium">Total Time</p>
                  </div>
                  <p className="text-2xl font-bold">{clientReport.summary.total_time_formatted || '0m'}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center space-x-2 text-red-600 mb-2">
                    <DollarSign className="w-5 h-5" />
                    <p className="text-sm font-medium">Pending Amount</p>
                  </div>
                  <p className="text-2xl font-bold">₹{clientReport.summary.pending_amount?.toLocaleString() || 0}</p>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-white p-6 rounded-lg shadow border">
                <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
                <div className="grid grid-cols-3 gap-6 mb-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Invoiced</p>
                    <p className="text-xl font-bold text-blue-600">₹{clientReport.summary.total_invoiced?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Received</p>
                    <p className="text-xl font-bold text-green-600">₹{clientReport.summary.total_received?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pending</p>
                    <p className="text-xl font-bold text-red-600">₹{clientReport.summary.pending_amount?.toLocaleString() || 0}</p>
                  </div>
                </div>
                {/* Reimbursements Summary */}
                {(clientReport.summary.total_reimbursements > 0) && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-600">Total Reimbursements (Tax/Govt Fees)</p>
                        <p className="text-xl font-bold text-amber-600">₹{clientReport.summary.total_reimbursements?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Pending Collection</p>
                        <p className="text-xl font-bold text-amber-700">₹{clientReport.summary.pending_reimbursements?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Tasks List with Subtasks */}
              <div className="bg-white rounded-lg shadow border">
                <div className="p-4 border-b">
                  <h3 className="text-lg font-semibold">All Tasks</h3>
                </div>
                <div className="divide-y">
                  {clientReport.tasks.map((task) => (
                    <div key={task.id} className="p-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleTaskExpand(task.id)}
                      >
                        <div className="flex items-center gap-2">
                          {(task.sub_tasks?.length > 0 || task.reimbursements?.length > 0 || task.audit_trail?.length > 0) && (
                            expandedTasks[task.id] 
                              ? <ChevronDown className="w-4 h-4 text-gray-500" />
                              : <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                          <div>
                            <p className="font-medium">{task.task_name || task.task_type_name}</p>
                            <p className="text-sm text-gray-500">{task.service_name} • {formatDate(task.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-2 py-1 text-xs rounded ${
                            task.status === 'Completed' || task.status === 'Done' ? 'bg-green-100 text-green-700' :
                            task.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                            task.is_billable === false ? 'bg-gray-100 text-gray-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {task.is_billable === false ? 'Non-billable' : task.status}
                          </span>
                          <span className="text-sm text-purple-600">
                            {formatTime(task.total_time_minutes || 0)}
                          </span>
                          <span className="text-sm font-medium">
                            {task.invoice ? `₹${task.invoice.amount.toLocaleString()}` : '-'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      {expandedTasks[task.id] && (
                        <div className="mt-3 ml-6 space-y-3">
                          {/* Subtasks */}
                          {task.sub_tasks?.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-3 subtask-container">
                              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Sub-tasks ({task.sub_tasks.length})
                              </p>
                              {task.sub_tasks.map((subtask, idx) => (
                                <div key={subtask.id || idx} className="flex items-start gap-2 py-2 border-b border-dashed border-gray-200 last:border-b-0 subtask-item">
                                  <span className="font-bold text-gray-700 min-w-[25px] subtask-number">{idx + 1}.</span>
                                  {subtask.is_completed ? (
                                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  ) : (
                                    <div className="w-4 h-4 border-2 border-gray-300 rounded-full mt-0.5 flex-shrink-0"></div>
                                  )}
                                  <div className="flex-1">
                                    <span className={`${subtask.is_completed ? 'line-through text-gray-400 subtask-completed' : 'text-gray-700 subtask-pending'}`}>
                                      {subtask.title}
                                    </span>
                                    {subtask.is_completed && subtask.completed_by && (
                                      <span className="text-xs text-gray-400 ml-2 subtask-info">
                                        (by {subtask.completed_by} on {formatDateTime(subtask.completed_at)})
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Reimbursements */}
                          {task.reimbursements?.length > 0 && (
                            <div className="bg-amber-50 rounded-lg p-3">
                              <p className="text-xs font-semibold text-amber-700 mb-2 uppercase flex items-center">
                                <Banknote className="w-3 h-3 mr-1" />
                                Reimbursements / Govt Fees ({task.reimbursements.length})
                              </p>
                              <div className="space-y-2">
                                {task.reimbursements.map((reimb, idx) => (
                                  <div key={reimb.id || idx} className="flex items-center justify-between py-1 border-b border-dashed border-amber-200 last:border-b-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-amber-800 bg-amber-100 px-2 py-0.5 rounded">{reimb.type}</span>
                                      <span className="text-sm text-gray-600">{reimb.description || '-'}</span>
                                      <span className="text-xs text-gray-400">{formatDate(reimb.date)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium">₹{(reimb.amount || 0).toLocaleString()}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded ${reimb.status === 'Collected' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {reimb.status || 'Pending'}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                                <div className="flex justify-end pt-2 border-t border-amber-200">
                                  <span className="text-sm font-bold text-amber-800">
                                    Total: ₹{task.reimbursements.reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Receipts */}
                          {task.receipts?.length > 0 && (
                            <div className="bg-green-50 rounded-lg p-3">
                              <p className="text-xs font-semibold text-green-700 mb-2 uppercase flex items-center">
                                <Receipt className="w-3 h-3 mr-1" />
                                Receipts ({task.receipts.length})
                              </p>
                              <div className="space-y-2">
                                {task.receipts.map((receipt, idx) => (
                                  <div key={receipt.id || idx} className="flex items-center justify-between py-1 border-b border-dashed border-green-200 last:border-b-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-green-800">{receipt.number || `Receipt ${idx + 1}`}</span>
                                      <span className="text-xs text-gray-500">{formatDate(receipt.date)}</span>
                                      <span className="text-xs text-gray-400">({receipt.mode})</span>
                                    </div>
                                    <span className="text-sm font-medium text-green-700">₹{(receipt.amount || 0).toLocaleString()}</span>
                                  </div>
                                ))}
                                <div className="flex justify-end pt-2 border-t border-green-200">
                                  <span className="text-sm font-bold text-green-800">
                                    Total Received: ₹{task.receipts.reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Task History / Audit Trail */}
                          {(() => {
                            // Build comprehensive task history from task data
                            const historyItems = [];
                            
                            // 1. Task Creation
                            if (task.created_at) {
                              historyItems.push({
                                action: `Task Created`,
                                details: task.task_name || task.task_type_name,
                                user: task.created_by === 'system' ? 'System' : (task.created_by_name || 'User'),
                                timestamp: task.created_at,
                                type: 'create'
                              });
                            }
                            
                            // 2. Status changes (if non-billable)
                            if (task.is_billable === false) {
                              historyItems.push({
                                action: 'Marked as Non-Billable',
                                details: task.non_billable_reason || 'No reason provided',
                                user: task.non_billable_by || 'User',
                                timestamp: task.non_billable_at || task.updated_at,
                                type: 'status'
                              });
                            }
                            
                            // 3. Invoice added
                            if (task.invoice) {
                              historyItems.push({
                                action: `Invoice Generated - ₹${task.invoice.amount?.toLocaleString()}`,
                                details: `Invoice No: ${task.invoice.number}`,
                                user: 'User',
                                timestamp: task.invoice.date,
                                type: 'invoice'
                              });
                            }
                            
                            // 4. Discount added
                            if (task.discount) {
                              historyItems.push({
                                action: `Discount Applied - ₹${task.discount.amount?.toLocaleString()}`,
                                details: task.discount.reason || 'No reason',
                                user: task.discount.added_by || 'User',
                                timestamp: task.discount.added_at || task.discount.date,
                                type: 'discount'
                              });
                            }
                            
                            // 5. Reimbursements added
                            (task.reimbursements || []).forEach((reimb, idx) => {
                              historyItems.push({
                                action: `Reimbursement Added - ₹${reimb.amount?.toLocaleString()}`,
                                details: `${reimb.type}: ${reimb.description || 'N/A'}`,
                                user: reimb.added_by || 'User',
                                timestamp: reimb.added_at || reimb.date,
                                type: 'reimbursement'
                              });
                              
                              if (reimb.status === 'Collected') {
                                historyItems.push({
                                  action: `Reimbursement Collected - ₹${reimb.amount?.toLocaleString()}`,
                                  details: `${reimb.type}: ${reimb.description || 'N/A'}`,
                                  user: reimb.collected_by || 'User',
                                  timestamp: reimb.collected_at,
                                  type: 'reimbursement_collected'
                                });
                              }
                            });
                            
                            // 6. Receipts added
                            (task.receipts || []).forEach((receipt, idx) => {
                              historyItems.push({
                                action: `Receipt Collected - ₹${receipt.amount?.toLocaleString()}`,
                                details: `Receipt No: ${receipt.number}, Mode: ${receipt.mode}`,
                                user: 'User',
                                timestamp: receipt.date,
                                type: 'receipt'
                              });
                            });
                            
                            // 7. Add audit trail logs if present
                            (task.audit_trail || []).forEach(log => {
                              // Avoid duplicates - only add if not already covered above
                              const isDuplicate = historyItems.some(h => 
                                h.timestamp === log.timestamp && h.action.includes(log.action)
                              );
                              if (!isDuplicate) {
                                historyItems.push({
                                  action: log.action,
                                  details: log.details || '',
                                  user: log.user_name || 'User',
                                  timestamp: log.timestamp,
                                  type: 'audit'
                                });
                              }
                            });
                            
                            // Sort by timestamp (newest first)
                            historyItems.sort((a, b) => {
                              const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
                              const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
                              return dateB - dateA;
                            });
                            
                            if (historyItems.length === 0) return null;
                            
                            const getTypeColor = (type) => {
                              switch(type) {
                                case 'create': return 'bg-green-400';
                                case 'invoice': return 'bg-blue-400';
                                case 'receipt': return 'bg-emerald-400';
                                case 'reimbursement': return 'bg-amber-400';
                                case 'reimbursement_collected': return 'bg-amber-600';
                                case 'discount': return 'bg-purple-400';
                                case 'status': return 'bg-gray-400';
                                default: return 'bg-blue-400';
                              }
                            };
                            
                            return (
                              <div className="bg-blue-50 rounded-lg p-3 task-history-section">
                                <p className="text-xs font-semibold text-blue-700 mb-2 uppercase flex items-center">
                                  <History className="w-3 h-3 mr-1" />
                                  Task History ({historyItems.length} events)
                                </p>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {historyItems.map((item, idx) => (
                                    <div key={idx} className="flex items-start gap-2 py-1.5 border-b border-dashed border-blue-200 last:border-b-0">
                                      <div className={`w-2 h-2 ${getTypeColor(item.type)} rounded-full mt-1.5 flex-shrink-0`}></div>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-800">{item.action}</p>
                                        {item.details && (
                                          <p className="text-xs text-gray-600">{item.details}</p>
                                        )}
                                        <p className="text-xs text-gray-500">
                                          {item.user} • {item.timestamp ? formatDateTime(item.timestamp) : 'N/A'}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!loading && !clientReport && selectedClient && (
            <div className="text-center py-12 text-gray-500">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p>No data found for this client.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
